/**
 * 백그라운드 동기화 시스템
 * - 네트워크 개선 감지시 자동 동기화 시작
 * - IndexedDB의 오프라인 레포트들을 서버로 순차 업로드
 * - 이미지 복원 및 업로드 처리
 * - 성공시 IndexedDB에서 개별 삭제
 */

import { addNetworkListener, removeNetworkListener } from './networkMonitor';
import { getAllOfflineReports, deleteOfflineReport, markOfflineImageUploaded } from './offlineStorage';
import { initializeRecovery, saveSyncState, updateSyncState, clearSyncState, startRecovery } from './syncRecovery';
import { checkBeforeUpload, markAsProcessed } from './duplicateChecker';
import { autoCompressIfNeeded } from './imageCompression';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

export class BackgroundSync {
  constructor() {
    this.isRunning = false;
    this.syncQueue = [];
    this.currentSyncReport = null;
    this.syncProgress = {
      total: 0,
      completed: 0,
      failed: 0,
      current: null
    };
    this.listeners = new Set();
    this.maxRetries = 3;
    this.retryDelay = 5000; // 5초
    this.authToken = null; // 인증 토큰 저장
    this.syncCheckTimeout = null; // 중복 실행 방지용 타임아웃
    this.lastNetworkState = null; // 이전 네트워크 상태 저장

    this.initializeBackgroundSync();
  }

  /**
   * 인증 토큰 설정
   */
  setAuthToken(token) {
    // console.log('[BackgroundSync] 인증 토큰 설정:', token ? '토큰 설정됨' : '토큰 없음');
    this.authToken = token;

    // 토큰이 설정된 후 처리할 작업들
    if (token) {
      // console.log('[BackgroundSync] 토큰 설정 완료, 대기 중인 작업들 처리...');

      // 1. 일반 동기화 체크
      this.checkAndStartSync();

      // 2. 대기 중인 복구 작업 처리
      if (window.FDES_RECOVERY_PENDING) {
        // console.log('[BackgroundSync] 대기 중인 복구 작업 시작');
        startRecovery(this, window.FDES_RECOVERY_PENDING);
        window.FDES_RECOVERY_PENDING = null;
      }
    }
  }

  /**
   * axios 요청 헬퍼 - GET
   */
  async apiGet(path, options = {}) {
    const url = API_URL + path;
    const config = {
      ...options,
      headers: {
        ...options.headers,
        ...(this.authToken && { 'x-access-token': this.authToken })
      }
    };

    const response = await axios.get(url, config);
    return response;
  }

  /**
   * axios 요청 헬퍼 - POST
   */
  async apiPost(path, data, options = {}) {
    const url = API_URL + path;
    const config = {
      ...options,
      headers: {
        ...options.headers,
        ...(this.authToken && { 'x-access-token': this.authToken })
      }
    };

    const response = await axios.post(url, data, config);
    return response;
  }

  /**
   * axios 요청 헬퍼 - PUT
   */
  async apiPut(path, data, options = {}) {
    const url = API_URL + path;
    const config = {
      ...options,
      headers: {
        ...options.headers,
        ...(this.authToken && { 'x-access-token': this.authToken })
      }
    };

    const response = await axios.put(url, data, config);
    return response;
  }

  /**
   * 백그라운드 동기화 초기화
   */
  initializeBackgroundSync() {
    // 네트워크 상태 변화 리스너 등록
    addNetworkListener(this.handleNetworkStateChange.bind(this));

    // 복구 시스템 초기화
    initializeRecovery(this);

    // 페이지 로드시 초기 동기화 체크
    this.checkAndStartSync();

    // console.log('[BackgroundSync] 백그라운드 동기화 시스템 초기화됨');
  }

  /**
   * 네트워크 상태 변화 핸들러
   */
  async handleNetworkStateChange(networkState) {
    // console.log('[BackgroundSync] 네트워크 상태 변화:', networkState);

    // 이전 상태와 비교
    const previousState = this.lastNetworkState || {};
    this.lastNetworkState = { ...networkState };

    // 동기화를 시작해야 하는 조건들:
    // 1. 오프라인에서 온라인으로 전환
    // 2. 느린 연결에서 빠른 연결로 개선
    // 3. 초기 로드시 온라인 상태이고 대기 중인 레포트가 있는 경우
    const shouldStartSync = (
      // 오프라인에서 온라인으로 전환
      (!previousState.isOnline && networkState.isOnline) ||
      // 오프라인 모드에서 온라인 모드로 전환 (느린 연결 → 빠른 연결)
      (previousState.shouldUseOfflineMode && !networkState.shouldUseOfflineMode) ||
      // 초기 로드시 (이전 상태 없음) + 온라인 상태
      (!previousState.isOnline && networkState.isOnline && !networkState.shouldUseOfflineMode)
    );

    if (shouldStartSync) {
      // console.log('[BackgroundSync] 네트워크 개선 감지, 동기화 시작 검토');
      // console.log('[BackgroundSync] 이전 상태:', previousState);
      // console.log('[BackgroundSync] 현재 상태:', networkState);

      // 이미 동기화 중이거나 최근에 체크했으면 스킵
      if (this.isRunning || this.syncCheckTimeout) {
        // console.log('[BackgroundSync] 동기화 이미 진행 중이거나 예약됨, 스킵');
        return;
      }

      await this.checkAndStartSync();
    }
  }

  /**
   * 동기화 필요 여부 체크 및 시작
   */
  async checkAndStartSync() {
    if (this.isRunning) {
      // console.log('[BackgroundSync] 이미 동기화 진행 중');
      return;
    }

    // 중복 실행 방지를 위한 짧은 지연
    if (this.syncCheckTimeout) {
      clearTimeout(this.syncCheckTimeout);
    }

    this.syncCheckTimeout = setTimeout(async () => {
      this.syncCheckTimeout = null; // 타임아웃 해제
      await this.performSync();
    }, 100);
  }

  /**
   * 실제 동기화 수행
   */
  async performSync() {
    if (this.isRunning) {
      // console.log('[BackgroundSync] 이미 동기화 진행 중 (재확인)');
      return;
    }

    // console.log('[BackgroundSync] 동기화 수행 시작 - 오프라인 레포트 조회 중...');

    try {
      // 오프라인 저장된 레포트들 조회
      const offlineReports = await getAllOfflineReports();

      // console.log(`[BackgroundSync] 오프라인 레포트 조회 완료: ${offlineReports.length}개`);

      if (offlineReports.length === 0) {
        // console.log('[BackgroundSync] 동기화할 레포트 없음');
        return;
      }

      // console.log(`[BackgroundSync] ${offlineReports.length}개 레포트 동기화 시작`);
      // console.log('[BackgroundSync] 인증 토큰:', this.authToken ? '설정됨' : '없음');

      // 인증 토큰이 없으면 동기화 중단
      if (!this.authToken) {
        console.log('[BackgroundSync] ⚠️ 인증 토큰이 없어 동기화 중단');
        console.log('[BackgroundSync] 💡 로그인이 필요합니다. 로그인 후 자동으로 동기화됩니다.');

        // 사용자에게 알림 (조용히 실패하지 않도록)
        this.notifyListeners({
          type: 'auth_required',
          message: 'Authentication required for sync. Please login.',
          timestamp: new Date().toISOString()
        });

        return;
      }

      await this.startSync(offlineReports);

    } catch (error) {
      // console.error('[BackgroundSync] 동기화 체크 실패:', error);
    }
  }

  /**
   * 동기화 시작
   */
  async startSync(reports) {
    // 이중 체크 - 다른 인스턴스가 이미 시작했을 수 있음
    if (this.isRunning) {
      // console.log('[BackgroundSync] 다른 인스턴스가 이미 동기화 중, 중단');
      return;
    }

    // 전역 플래그로 중복 실행 방지
    if (window.FDES_SYNC_RUNNING) {
      // console.log('[BackgroundSync] 전역 동기화가 이미 진행 중, 중단');
      return;
    }

    this.isRunning = true;
    window.FDES_SYNC_RUNNING = true;

    this.syncQueue = [...reports];
    this.syncProgress = {
      total: reports.length,
      completed: 0,
      failed: 0,
      current: null
    };

    this.notifyListeners('sync_started', this.syncProgress);

    // 복구 시스템에 동기화 시작 알림
    saveSyncState(this.syncProgress);

    try {
      // 순차적으로 레포트 동기화
      for (const report of reports) {
        if (!this.isRunning) {
          // console.log('[BackgroundSync] 동기화 중단됨');
          break;
        }

        await this.syncSingleReport(report);

        // 각 레포트 완료 후 상태 업데이트
        updateSyncState(this.syncProgress);
      }

      // console.log(`[BackgroundSync] 동기화 완료: ${this.syncProgress.completed}개 성공, ${this.syncProgress.failed}개 실패`);

    } catch (error) {
      // console.error('[BackgroundSync] 동기화 과정 중 오류:', error);
    } finally {
      this.isRunning = false;
      window.FDES_SYNC_RUNNING = false; // 전역 플래그 해제
      this.currentSyncReport = null;

      // 동기화 완료 시 복구 상태 정리
      clearSyncState();

      this.notifyListeners('sync_completed', this.syncProgress);
    }
  }

  /**
   * 단일 레포트 동기화
   * 재시도는 이 함수 안에서 대기(await) 방식으로 수행되어,
   * 재시도가 남아있는 동안에는 동기화 세션이 "완료" 처리되지 않는다.
   */
  async syncSingleReport(report) {
    this.currentSyncReport = report;
    this.syncProgress.current = {
      id: report.id,
      name: report.name,
      status: 'syncing'
    };

    this.notifyListeners('sync_progress', this.syncProgress);

    let attempts = 0; // 실패 횟수 (재시도 = attempts - 1)

    while (this.isRunning) {
      try {
        // console.log(`[BackgroundSync] 레포트 동기화 시작: ${report.name} (${report.id})`);

        // 0. 중복 체크 수행
        const duplicateCheck = await checkBeforeUpload(report, this.apiGet.bind(this));

        if (!duplicateCheck.shouldUpload) {
          // console.log(`[BackgroundSync] 업로드 건너뜀: ${duplicateCheck.reason} - ${duplicateCheck.message || ''}`);

          // IndexedDB에서 삭제 (이미 서버에 있거나 처리되었음)
          await deleteOfflineReport(report.id);

          this.syncProgress.completed++;
          // console.log(`[BackgroundSync] 레포트 동기화 완료 (스킵): ${report.name}`);

          this.notifyListeners('report_synced', {
            report,
            success: true,
            skipped: true,
            reason: duplicateCheck.reason
          });

          return;
        }

        // console.log(`[BackgroundSync] 중복 체크 통과, 업로드 진행: ${duplicateCheck.reason}`);

        // 1. 이미지 업로드 (있는 경우)
        await this.uploadReportImages(report);

        // 2. 레포트 서버 업로드
        const uploadResult = await this.uploadReportToServer(report);

        if (!uploadResult.success) {
          throw new Error(uploadResult.error || '서버 업로드 실패');
        }

        // 3. 성공시 IndexedDB에서 삭제
        await deleteOfflineReport(report.id);

        // 4. 중복 방지를 위해 처리됨으로 마킹
        markAsProcessed(report.id);

        this.syncProgress.completed++;

        // console.log(`[BackgroundSync] 레포트 동기화 성공: ${report.name}`);

        this.notifyListeners('report_synced', {
          report,
          success: true,
          uploadResult,
          oldReportId: report.id, // 🔥 이전 오프라인 ID
          newReportId: uploadResult.data?.id // 🔥 서버가 생성한 새 ID
        });

        return;

      } catch (error) {
        // console.error(`[BackgroundSync] 레포트 동기화 실패: ${report.name}`, error);

        // 에러 분류 및 처리 전략 결정
        const errorStrategy = this.analyzeError(error);
        attempts++;

        if (!errorStrategy.shouldRetry || attempts > this.maxRetries) {
          // console.log(`[BackgroundSync] 실패 확정 (${errorStrategy.reason}), 레포트는 다음 동기화를 위해 보존됨`);
          this.handleFinalFailure(report, error, errorStrategy);
          return;
        }

        const delay = errorStrategy.retryDelay || this.retryDelay;
        // console.log(`[BackgroundSync] ${delay/1000}초 후 재시도 (${attempts}/${this.maxRetries}) - 사유: ${errorStrategy.reason}`);

        this.notifyListeners('report_retry', {
          report,
          attempt: attempts,
          maxRetries: this.maxRetries,
          delay,
          reason: errorStrategy.reason
        });

        await this.sleep(delay);
      }
    }

    // 동기화가 중단되어 전송하지 못한 경우 - 레포트는 IndexedDB에 남아 다음 동기화에서 처리됨
  }

  /**
   * 지정 시간 대기
   */
  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * 에러 분석 및 처리 전략 결정
   */
  analyzeError(error) {
    // HTTP 상태 코드별 분석
    if (error.response) {
      const status = error.response.status;

      switch (status) {
        case 401:
        case 403:
          return {
            shouldRetry: false,
            reason: 'authentication_error',
            userAction: 'relogin_required',
            message: '인증이 만료되었습니다. 다시 로그인해주세요.'
          };

        case 409:
          return {
            shouldRetry: false,
            reason: 'conflict_error',
            userAction: 'manual_review',
            message: '데이터 충돌이 발생했습니다.'
          };

        case 413:
          return {
            shouldRetry: false,
            reason: 'file_too_large',
            userAction: 'reduce_file_size',
            message: '파일 크기가 너무 큽니다.'
          };

        case 500:
        case 502:
        case 503:
          return {
            shouldRetry: true,
            reason: 'server_error',
            retryDelay: 10000, // 10초 (서버 에러는 더 길게)
            message: '서버 오류가 발생했습니다. 재시도합니다.'
          };

        case 429:
          return {
            shouldRetry: true,
            reason: 'rate_limit',
            retryDelay: 30000, // 30초 (API 제한)
            message: 'API 호출 제한에 도달했습니다.'
          };

        default:
          return {
            shouldRetry: status >= 500, // 5xx 에러만 재시도
            reason: `http_${status}`,
            message: `HTTP ${status} 오류가 발생했습니다.`
          };
      }
    }

    // 네트워크 에러
    if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
      return {
        shouldRetry: true,
        reason: 'network_error',
        retryDelay: 15000, // 15초
        message: '네트워크 오류가 발생했습니다.'
      };
    }

    // 타임아웃 에러
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      return {
        shouldRetry: true,
        reason: 'timeout_error',
        retryDelay: 20000, // 20초
        message: '요청 시간이 초과되었습니다.'
      };
    }

    // 기타 에러
    return {
      shouldRetry: true,
      reason: 'unknown_error',
      message: '알 수 없는 오류가 발생했습니다.'
    };
  }

  /**
   * 최종 실패 처리
   */
  handleFinalFailure(report, error, errorStrategy) {
    this.syncProgress.failed++;

    // 에러 유형에 따른 추가 처리
    if (errorStrategy.userAction === 'relogin_required') {
      // 인증 만료의 경우 전체 동기화 중단
      // console.log('[BackgroundSync] 인증 오류로 동기화 중단');
      this.stopSync();
    }

    this.notifyListeners('report_synced', {
      report,
      success: false,
      error: error.message,
      errorStrategy,
      finalFailure: true
    });

    // console.log(`[BackgroundSync] 레포트 최종 실패: ${report.name} - ${errorStrategy.reason}`);
  }

  /**
   * 레포트의 이미지들 업로드 (🆕 진행률 표시 추가)
   */
  async uploadReportImages(report) {
    if (!report.offlineMetadata || !report.offlineMetadata.hasImages) {
      return;
    }

    // 🆕 총 이미지 개수 계산
    let totalImages = 0;
    let uploadedImages = 0;
    for (const section of report.items) {
      if (!section.items) continue;
      for (const field of section.items) {
        if (field.type === 'image' && field.answer && field.answer.file && !field.answer.isUploaded) {
          totalImages++;
        }
      }
    }

    // console.log(`[BackgroundSync] 이미지 업로드 시작: ${totalImages}개`);

    // 🆕 이미지 업로드 시작 이벤트
    this.notifyListeners('image_upload_started', {
      reportId: report.id,
      reportName: report.name,
      totalImages: totalImages
    });

    for (const [sectionIdx, section] of report.items.entries()) {
      if (!section.items) continue;

      for (const [fieldIdx, field] of section.items.entries()) {
        if (field.type === 'image' && field.answer && field.answer.file && !field.answer.isUploaded) {

          try {
            const originalFile = field.answer.file;

            // 🆕 현재 이미지 업로드 시작 이벤트
            this.notifyListeners('image_upload_progress', {
              reportId: report.id,
              reportName: report.name,
              currentImage: uploadedImages + 1,
              totalImages: totalImages,
              fileName: originalFile.name,
              status: 'compressing'
            });

            // console.log(`[BackgroundSync] ⚡ 동기화 이미지 압축 시작 (${uploadedImages + 1}/${totalImages}): ${originalFile.name}`);
            // console.log(`[BackgroundSync] 📊 IndexedDB에서 복원된 크기: ${(originalFile.size / 1024 / 1024).toFixed(2)}MB`);

            // 🆕 무조건 압축 시도 (1MB 이상이면 무조건 압축)
            let compressedFile;
            const ONE_MB = 1 * 1024 * 1024;

            if (originalFile.size > ONE_MB) {
              // console.log(`[BackgroundSync] 🔧 1MB 초과, 강제 압축 실행...`);
              // 1MB 이상이면 무조건 압축 (더 공격적인 옵션)
              const { compressImage } = await import('./imageCompression');
              compressedFile = await compressImage(originalFile, {
                quality: 0.75,  // 더 낮은 품질
                maxWidth: 1600,  // 더 작은 해상도
                maxHeight: 1600
              });
            } else {
              // 1MB 이하면 자동 판단
              compressedFile = await autoCompressIfNeeded(originalFile);
            }

            const sizeReduction = originalFile.size !== compressedFile.size
              ? (((originalFile.size - compressedFile.size) / originalFile.size) * 100).toFixed(1) + '%'
              : '압축 안됨';

            // console.log(`[BackgroundSync] ✅ 압축 완료: ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`);
            // console.log(`[BackgroundSync] 📉 압축률: ${sizeReduction}`);

            // 🆕 업로드 중 이벤트
            this.notifyListeners('image_upload_progress', {
              reportId: report.id,
              reportName: report.name,
              currentImage: uploadedImages + 1,
              totalImages: totalImages,
              fileName: originalFile.name,
              status: 'uploading',
              compressedSize: compressedFile.size,
              originalSize: originalFile.size,
              compressionRate: sizeReduction
            });

            const formData = new FormData();
            formData.append("file", compressedFile);

            // console.log(`[BackgroundSync] 📤 서버 업로드 시도 중 (${uploadedImages + 1}/${totalImages})... (${(compressedFile.size / 1024 / 1024).toFixed(2)}MB)`);

            const resp = await this.apiPost("/reports/attachments", formData);

            if (resp && resp.data) {
              // 업로드 성공시 report 객체 업데이트
              report.items[sectionIdx].items[fieldIdx].answer.name = resp.data.name;
              delete report.items[sectionIdx].items[fieldIdx].answer.file;
              report.items[sectionIdx].items[fieldIdx].answer.isUploaded = true;

              // 🆕 업로드 진행 상황을 IndexedDB에 중간 저장
              // 브라우저 재시작 후에도 업로드 완료된 이미지부터 다시 시작하지 않도록 함
              try {
                await markOfflineImageUploaded(
                  report.id,
                  `items[${sectionIdx}].items[${fieldIdx}]`,
                  resp.data.name
                );
              } catch (progressError) {
                // 중간 저장 실패는 치명적이지 않음 (다음 세션에서 해당 이미지가 다시 업로드될 뿐)
                // console.error('[BackgroundSync] 이미지 진행 상황 저장 실패:', progressError);
              }

              uploadedImages++;

              // 🆕 개별 이미지 업로드 완료 이벤트
              this.notifyListeners('image_upload_progress', {
                reportId: report.id,
                reportName: report.name,
                currentImage: uploadedImages,
                totalImages: totalImages,
                fileName: originalFile.name,
                status: 'completed',
                serverFileName: resp.data.name
              });

              // console.log(`[BackgroundSync] 이미지 업로드 성공 (${uploadedImages}/${totalImages}): ${resp.data.name}`);
            }

          } catch (error) {
            // console.error('[BackgroundSync] 이미지 업로드 실패:', error);

            // 🆕 이미지 업로드 실패 이벤트
            this.notifyListeners('image_upload_progress', {
              reportId: report.id,
              reportName: report.name,
              currentImage: uploadedImages + 1,
              totalImages: totalImages,
              fileName: field.answer.file.name,
              status: 'failed',
              error: error.message
            });

            throw error; // 이미지 업로드 실패시 전체 레포트 동기화 실패
          }
        }
      }
    }

    // 🆕 모든 이미지 업로드 완료 이벤트
    this.notifyListeners('image_upload_completed', {
      reportId: report.id,
      reportName: report.name,
      uploadedImages: uploadedImages,
      totalImages: totalImages
    });

    // console.log(`[BackgroundSync] 모든 이미지 업로드 완료: ${uploadedImages}/${totalImages}`);
  }

  /**
   * 레포트를 서버에 업로드
   */
  async uploadReportToServer(report) {
    // 오프라인 메타데이터 제거
    const cleanReport = { ...report };
    delete cleanReport.offlineMetadata;

    let response;

    // 기존 레포트인지 새 레포트인지 확인
    // 통신 에러는 그대로 전파하여 analyzeError가 HTTP 상태 코드(401 등)를 판별할 수 있게 한다
    if (report.id && !report.id.startsWith('offline_')) {
      // 기존 레포트 업데이트
      response = await this.apiPut(`/reports/${report.id}`, cleanReport);
    } else {
      // 새 레포트 생성 (오프라인에서 생성된 ID 제거)
      delete cleanReport.id;
      response = await this.apiPost('/reports', cleanReport);
    }

    if (response && response.data) {
      return {
        success: true,
        data: response.data
      };
    }

    return {
      success: false,
      error: '서버 응답 오류'
    };
  }

  /**
   * 동기화 중단
   */
  stopSync() {
    // console.log('[BackgroundSync] 동기화 중단 요청');
    this.isRunning = false;
    window.FDES_SYNC_RUNNING = false; // 전역 플래그 해제
    this.notifyListeners('sync_stopped', this.syncProgress);
  }

  /**
   * 수동 동기화 트리거
   */
  async manualSync() {
    // console.log('[BackgroundSync] 수동 동기화 시작');
    await this.checkAndStartSync();
  }

  /**
   * 현재 동기화 상태 반환
   */
  getSyncStatus() {
    return {
      isRunning: this.isRunning,
      progress: this.syncProgress,
      currentReport: this.currentSyncReport
    };
  }

  /**
   * 대기 중인 레포트 개수 조회
   */
  async getPendingReportsCount() {
    try {
      const offlineReports = await getAllOfflineReports();
      return offlineReports.length;
    } catch (error) {
      // console.error('[BackgroundSync] 대기 레포트 개수 조회 실패:', error);
      return 0;
    }
  }

  /**
   * 동기화 상태 변화 리스너 등록
   */
  addSyncListener(callback) {
    this.listeners.add(callback);
  }

  /**
   * 동기화 상태 변화 리스너 제거
   */
  removeSyncListener(callback) {
    this.listeners.delete(callback);
  }

  /**
   * 모든 리스너들에게 상태 변화 알림
   */
  notifyListeners(event, data) {
    this.listeners.forEach(callback => {
      try {
        callback(event, data);
      } catch (error) {
        // console.error('[BackgroundSync] 리스너 실행 오류:', error);
      }
    });
  }

  /**
   * 정리
   */
  destroy() {
    removeNetworkListener(this.handleNetworkStateChange);
    this.stopSync();
    this.listeners.clear();

    if (this.syncCheckTimeout) {
      clearTimeout(this.syncCheckTimeout);
      this.syncCheckTimeout = null;
    }

    // console.log('[BackgroundSync] 백그라운드 동기화 시스템 정리됨');
  }
}

// 진정한 싱글톤 인스턴스 (Hot Reload 대응)
let backgroundSync;

if (window.FDES_BACKGROUND_SYNC) {
  // 이미 인스턴스가 있으면 재사용
  backgroundSync = window.FDES_BACKGROUND_SYNC;
  // console.log('[BackgroundSync] 기존 인스턴스 재사용');
} else {
  // 새 인스턴스 생성 및 전역 저장
  backgroundSync = new BackgroundSync();
  window.FDES_BACKGROUND_SYNC = backgroundSync;
  // console.log('[BackgroundSync] 새 인스턴스 생성');
}

// 편의 함수들
export const startManualSync = () => backgroundSync.manualSync();
export const stopSync = () => backgroundSync.stopSync();
export const getSyncStatus = () => backgroundSync.getSyncStatus();
export const getPendingReportsCount = () => backgroundSync.getPendingReportsCount();
export const addSyncListener = (callback) => backgroundSync.addSyncListener(callback);
export const removeSyncListener = (callback) => backgroundSync.removeSyncListener(callback);
export const setAuthToken = (token) => backgroundSync.setAuthToken(token);

export default backgroundSync;